import type { ReactNode } from 'react'
import { useState } from 'react'
import { SectionCard } from '../../shared/section-card'
import { useCoachingStore } from '../../store'
import type { ReviewComment } from '../contracts'
import { CritiqueFlag } from '../critique-flag'
import {
  type SuggestedResourceRow,
  SuggestedResources,
  type SuggestedResourceType,
} from './suggested-resources'

/**
 * Answer · STAR breakdown (spec §8.5, conflict-story seed) — a
 * behavioral-specific UI payload, not modeled on `ReviewSubmission` (see
 * A1's note on code-section.tsx re: per-type payloads living in the body
 * component that owns them). Four transcript beats, each individually
 * commentable; one ✦ SUGGESTED Reflection beat the coach can accept (which
 * appends a fifth, purple-badged beat) or dismiss; and the red dashed
 * Result-gap callout reusing A6's shared `SuggestedResources` rows.
 */

interface StarBeat {
  letter: string
  name: string
  dur: string
  target: string
  text: string
}

const STAR_BEATS: StarBeat[] = [
  {
    letter: 'S',
    name: 'SITUATION',
    dur: '1:34',
    target: 'Answer · Situation',
    text: 'Six weeks before a marquee launch, a principal engineer proposed swapping our REST layer for GraphQL to unblock a partner team.',
  },
  {
    letter: 'T',
    name: 'TASK',
    dur: '0:22',
    target: 'Answer · Task',
    text: 'As frontend lead, I had to either absorb the migration risk or push back on someone two levels senior — without stalling the partner team.',
  },
  {
    letter: 'A',
    name: 'ACTION',
    dur: '1:41',
    target: 'Answer · Action',
    text: 'I timeboxed a two-day spike, brought data to the design review — 40% of our endpoints had no GraphQL equivalent — and proposed a BFF facade for just the partner’s three endpoints. When the room stayed split, I escalated jointly with the principal, presenting both options.',
  },
  {
    letter: 'R',
    name: 'RESULT',
    dur: '0:35',
    target: 'Answer · Result',
    text: 'We shipped the facade. Launch stayed on schedule and the partner team unblocked. The full migration happened next quarter.',
  },
]

const REFLECTION_TEXT =
  'Reflection — close with one sentence on what you’d do differently (e.g. surface the partner dependency a sprint earlier).'

const GAP_RESOURCES: Array<{
  id: string
  type: SuggestedResourceType
  label: string
  meta: string
}> = [
  {
    id: 'result-gap-read',
    type: 'Read',
    label: 'Quantifying impact — Results cheat sheet',
    meta: '~15 min',
  },
  {
    id: 'result-gap-drill',
    type: 'Drill',
    label: 'Rewrite 3 story Results with metrics',
    meta: 'Sprint 15',
  },
  {
    id: 'result-gap-video',
    type: 'Video',
    label: 'STAR endings that land',
    meta: '9 min',
  },
]

type ReflectionStatus = 'pending' | 'accepted' | 'dismissed'

export function StarAnswer(props: {
  submissionId: string
  comments?: ReviewComment[]
  anchorRef?: React.Ref<HTMLDivElement>
}): ReactNode {
  const { submissionId, comments = [], anchorRef } = props
  const { openComposer, attachments, toggleAttachment } =
    useCoachingStore().review
  const sectionComments = comments.filter((c) => c.section === 'answer')
  const [reflectionStatus, setReflectionStatus] =
    useState<ReflectionStatus>('pending')

  const resourceRows: SuggestedResourceRow[] = GAP_RESOURCES.map((r) => {
    const key = `resource:${submissionId}:${r.id}`
    return {
      id: r.id,
      type: r.type,
      label: r.label,
      meta: r.meta,
      assigned: attachments[key] ?? false,
      onToggleAssign: () => toggleAttachment(key),
    }
  })

  return (
    <SectionCard
      title="Answer · STAR breakdown"
      commentCount={sectionComments.length}
      onAddComment={() =>
        openComposer('answer', 'Answer · Overall', 'critique')
      }
      anchorRef={anchorRef}
    >
      {sectionComments.map((c) => (
        <CritiqueFlag key={c.id} comment={c} />
      ))}

      <div className="mb-2.5 text-[11.5px] text-(--cd-text-faint)">
        Transcribed from the recording. Click any beat to comment on it
        specifically.
      </div>

      <div className="mb-2.5 flex flex-col gap-1.5">
        {STAR_BEATS.map((beat) => (
          <button
            key={beat.name}
            type="button"
            onClick={() => openComposer('answer', beat.target, 'critique')}
            className="flex items-start gap-2.5 rounded-lg border border-(--cd-border-2) bg-(--cd-inset) p-2.5 text-left"
          >
            <span className="mt-px flex size-[22px] shrink-0 items-center justify-center rounded-md bg-(--cd-bg) text-[11px] font-bold text-(--cd-text-muted)">
              {beat.letter}
            </span>
            <span className="flex-1">
              <span className="mb-0.5 block text-[10.5px] font-semibold tracking-wide text-(--cd-text-faint)">
                {beat.name} · {beat.dur}
              </span>
              <span className="block text-[13px] leading-relaxed text-(--cd-text-2)">
                {beat.text}
              </span>
            </span>
          </button>
        ))}

        {reflectionStatus === 'accepted' && (
          <button
            type="button"
            onClick={() =>
              openComposer('answer', 'Answer · Reflection', 'critique')
            }
            className="flex items-start gap-2.5 rounded-lg border p-2.5 text-left"
            style={{
              borderColor:
                'color-mix(in oklab, var(--cd-purple) 30%, transparent)',
              background: 'var(--cd-inset)',
            }}
          >
            <span
              className="mt-px flex size-[22px] shrink-0 items-center justify-center rounded-md text-[11px] font-bold"
              style={{
                background:
                  'color-mix(in oklab, var(--cd-purple) 15%, transparent)',
                color: 'var(--cd-purple)',
              }}
            >
              +
            </span>
            <span className="flex-1">
              <span className="mb-0.5 block text-[10.5px] font-semibold tracking-wide text-(--cd-text-faint)">
                REFLECTION · new
              </span>
              <span className="block text-[13px] leading-relaxed text-(--cd-text-2)">
                {REFLECTION_TEXT}
              </span>
            </span>
            <span className="shrink-0 text-[10px] font-bold text-(--cd-purple)">
              ✦ added
            </span>
          </button>
        )}

        {reflectionStatus === 'pending' && (
          <div
            className="flex items-center gap-2.5 rounded-lg border border-dashed p-2.5"
            style={{
              background:
                'color-mix(in oklab, var(--cd-purple) 5%, transparent)',
              borderColor:
                'color-mix(in oklab, var(--cd-purple) 35%, transparent)',
            }}
          >
            <span className="shrink-0 text-[10px] font-bold text-(--cd-purple)">
              ✦ SUGGESTED
            </span>
            <span className="flex-1 text-[13px] leading-relaxed text-(--cd-text-2)">
              {REFLECTION_TEXT}
            </span>
            <button
              type="button"
              onClick={() => setReflectionStatus('accepted')}
              className="shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold"
              style={{
                background:
                  'color-mix(in oklab, var(--cd-green) 8%, transparent)',
                color: 'var(--cd-green)',
                borderColor:
                  'color-mix(in oklab, var(--cd-green) 30%, transparent)',
              }}
            >
              + Add
            </button>
            <button
              type="button"
              onClick={() => setReflectionStatus('dismissed')}
              className="shrink-0 text-[11px] text-(--cd-text-faint) hover:text-(--cd-red)"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      <div
        className="rounded-[10px] border border-dashed p-3.5"
        style={{
          background: 'color-mix(in oklab, var(--cd-red) 5%, transparent)',
          borderColor: 'color-mix(in oklab, var(--cd-red) 35%, transparent)',
        }}
      >
        <div className="mb-1 flex items-center justify-between gap-2.5">
          <div className="text-xs font-semibold text-(--cd-red)">
            Result — no quantified outcome
          </div>
          <button
            type="button"
            onClick={() =>
              openComposer('answer', 'Answer · Result gap', 'critique')
            }
            className="shrink-0 rounded-full border border-(--cd-border-2) px-2.5 py-1 text-[11.5px] text-(--cd-text-muted)"
          >
            + Comment on gap
          </button>
        </div>
        <div className="mb-2.5 text-[12.5px] leading-relaxed text-(--cd-text-muted)">
          "Launch stayed on schedule" carries no numbers — no latency, adoption,
          or effort-saved figure an interviewer can anchor on.
        </div>
        <div className="mb-1.5 text-[10.5px] font-bold tracking-wide text-(--cd-purple)">
          ✦ SUGGESTED RESOURCES TO CLOSE THE GAP
        </div>
        <SuggestedResources rows={resourceRows} />
      </div>
    </SectionCard>
  )
}
